import React, { useEffect, useState } from 'react';
import { Modal, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UserProfileMainPage from '../../components/user-profile-main/UserProfileMainPage';
import scopedCss from './styles';
import {
  cap,
  planCls,
  monthlyOf,
  money,
  monthsSince,
  daysUntil,
  fmtDate,
  fmtDur,
  fmtWhen,
  ago,
  lastMsgTime,
  hasReply,
  msgCount,
  metrics,
} from './memberEngagementUtils';
import {
  subPill,
  acctPill,
  Avatar,
  convTag,
  Chevron,
  renderMsgContent,
  profileModalStyle,
} from './memberEngagementUi';

/**
 * Reusable Member Engagement detail drawer.
 *
 * This is the exact slide-in panel from the Member Engagement list (member
 * response stats, subscription card and expandable message threads). It is
 * self-contained — it carries its own scoped styles (`.met-scope`) and its own
 * "View profile" modal — so it can be dropped into any page (e.g. the
 * Subscription Report) by passing a single member object.
 *
 * Props:
 *   member  — one member object from getMemberEngagement (null = closed)
 *   now     — server snapshot ISO string (for waiting-time / last-active math)
 *   onClose — called when the overlay / close button / Escape dismisses it
 *   open    — (optional) force the drawer open even before `member` arrives, so a
 *             caller can slide it in while it fetches (defaults to !!member)
 *   loading — (optional) show a loading state inside the open drawer
 *   fallback— (optional) { id, name, profilePicture, isDeletedUser } used to show
 *             the member's identity in the header even when no engagement data is
 *             returned (e.g. a deleted member with no chat history)
 */
const initialsOf = (name = '') =>
  (name.trim().split(/\s+/).slice(0, 2).map((w) => (w[0] || '').toUpperCase()).join('') || 'M');

// Deleting an account HARD-deletes its chat data (AuthController deleteAccount runs
// deleteMany over `conversations` + `user_chat_history`), so engagement for such a
// member is GONE — not empty. We say so explicitly rather than showing the generic
// "no conversations" line, which reads as "they never messaged anyone".
//
// deleteAccount now archives the thread before destroying it, so members who leave
// from that point on DO have a transcript and never reach this message. It is
// therefore only shown to members deleted BEFORE archiving shipped, whose messages
// really were destroyed with no copy taken — hence the past tense, which stays
// accurate for them permanently.
const DELETED_CHAT_MSG = 'This account was deleted, and its chat history was permanently removed at that time. '
  + 'Engagement metrics can no longer be calculated. The subscription and payment records above are retained.';
// The rate is missing because the member never SPOKE, which is not the same as
// having no conversations — since inbound threads are listed, a member can be
// looking at 12 of them under a rate of "—". Saying "no conversations yet" there
// would contradict the list right below it.
const UNKNOWN_TIP = (deleted) => (deleted
  ? 'Unavailable — chat history was permanently deleted with this account'
  : 'This member has never messaged anyone, so there is no response rate to calculate');

const MemberEngagementDrawer = ({ member, now, onClose, open: openProp, loading, fallback }) => {
  const [openConvs, setOpenConvs] = useState({});
  const [profileUserId, setProfileUserId] = useState(null);

  const open = openProp !== undefined ? openProp : !!member;
  const nowD = now ? new Date(now) : new Date();

  // Start with every conversation collapsed whenever a new member opens.
  useEffect(() => {
    if (member) setOpenConvs({});
  }, [member]);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const openProfile = (e, userId) => {
    if (e) e.stopPropagation();
    if (userId) setProfileUserId(userId);
  };
  const closeProfile = () => setProfileUserId(null);

  // Chat Messenger is organised BOT-first — pick a bot on the left, then one of the
  // people it is talking to — so only a conversation whose match is the bot has a
  // place to land there. Nothing is offered on a real-member thread, and nothing on a
  // deleted member's: deleteAccount destroys the live chat rows, so the thread this
  // drawer is rendering from the archive no longer exists in Chat Messenger at all.
  // Returns null when there is no link to offer.
  const chatHref = (c) => (
    c.matchIsBot && member?.id && !member?.isDeletedUser
      ? `/chat?bot=${encodeURIComponent(c.matchUserId)}&member=${encodeURIComponent(member.id)}`
      : null);

  const renderThread = (c, memberName) => {
    const nodes = [];
    let prev = null;
    c.msgs.forEach((msg, i) => {
      if (prev && prev.f === 'me' && msg.f === 'them') {
        const lat = (new Date(msg.t) - new Date(prev.t)) / 60000;
        const cls = lat > 720 ? 'slow' : 'fast';
        nodes.push(
          <div className={`lat ${cls}`} key={`lat-${i}`}>Match replied after <b>{fmtDur(lat)}</b></div>
        );
      }
      const senderName = msg.f === 'me' ? memberName : c.match;
      nodes.push(
        <div className={`bubble ${msg.f === 'me' ? 'me' : 'them'}`} key={`b-${i}`}>
          <span className="sender">{senderName}</span>
          {renderMsgContent(msg)}<time>{fmtWhen(msg.t)}</time>
        </div>
      );
      prev = msg;
    });
    if (!hasReply(c)) {
      const wait = (nowD - new Date(lastMsgTime(c))) / 60000;
      nodes.push(<div className="waiting" key="wait">{`No reply yet · waiting ${fmtDur(wait)}`}</div>);
    }
    return nodes;
  };

  // One collapsible conversation row. Keyed on the MATCH's id, not the list index:
  // the drawer now renders two lists (outbound, then inbound-only) and an index
  // would collide between them, so opening a thread in one would expand a different
  // thread in the other. A pair appears at most once per member, so the id is unique.
  const renderConv = (c, memberName) => {
    const key = String(c.matchUserId);
    const isOpen = !!openConvs[key];
    return (
      <div className={`conv${isOpen ? ' open' : ''}`} key={key}>
        <div
          className="conv-head"
          onClick={() => setOpenConvs((o) => ({ ...o, [key]: !o[key] }))}
        >
          <Avatar src={c.matchProfilePicture} initials={c.mi} className="sm them" />
          <div>
            <div className="who">{c.match} <span className={`utype ${c.matchIsBot ? 'bot' : 'real'}`}>{c.matchIsBot ? 'Bot' : 'Real'}</span></div>
            {/* The true thread length — `msgs` only carries the most recent 60. */}
            <div className="cm">
              {msgCount(c)} messages{msgCount(c) > c.msgs.length ? ` (showing last ${c.msgs.length})` : ''} · last {ago(new Date(lastMsgTime(c)).toISOString(), nowD)} ·
              <span className="vprofile" role="button" tabIndex={0} onClick={(e) => openProfile(e, c.matchUserId)}>View profile →</span>
              {chatHref(c) ? (
                <>
                  {' · '}
                  {/* A real link, opened in a new tab: answering a match in Chat Messenger
                      is a separate job from reading this drawer, and navigating in place
                      would close the drawer and lose the member you were looking at.
                      stopPropagation so the click does not also toggle the thread open. */}
                  <a
                    className="vprofile"
                    href={chatHref(c)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open this conversation in Chat Messenger (new tab)"
                    onClick={(e) => e.stopPropagation()}
                  >Open in chat</a>
                </>
              ) : null}
            </div>
          </div>
          <span style={{ marginLeft: 'auto' }}>{convTag(c)}</span>
          <Chevron />
        </div>
        <div className="thread">{isOpen ? renderThread(c, memberName) : null}</div>
      </div>
    );
  };

  const renderDrawer = () => {
    if (!member) return null;
    const m = member;
    const k = metrics(m);
    // Deleting an account HARD-deletes its chats (AuthController deleteAccount:
    // conversations + user_chat_history deleteMany). deleteAccount now copies the
    // thread to the conversation archive first, so a member deleted since that
    // shipped still arrives here with full `convos` and reads exactly like a live
    // member — metrics included.
    //
    // Members who left BEFORE archiving existed have no transcript to recover, and
    // for them the old rule still holds: their metrics are UNKNOWN, not zero.
    // Rendering "0%" would assert the member never replied, which we cannot know,
    // and metrics() would additionally flag them 'risk' (rate 0 < 0.3) and date
    // "last active" to the 1970 epoch (last = 0). Show "—" instead.
    const gone = !!m.isDeletedUser;
    const hasConvos = m.convos.length > 0;
    // Deleted AND nothing archived — the only case where the history is truly lost.
    const chatLost = gone && !hasConvos;
    // A response rate needs a denominator, and that denominator is OUTBOUND threads
    // (`k.known` === reached > 0). Reading it off `m.convos` instead would print a
    // red 0% for a member whose only threads are inbound — asserting they were
    // ignored by everyone, when in fact they are the one who never spoke.
    const rateKnown = k.known;
    const rate = Math.round(k.rate * 100);
    const vcls = rateKnown ? (k.status === 'good' ? 'good' : k.status === 'risk' ? 'risk' : '') : '';
    const sub = m.sub;
    // A subscription that has ENDED has no renewal date. `renews` is the paid
    // period's end, which for a member who cancelled mid-term still sits in the
    // future — printing it as "Renews · in 20d" contradicted the Subscription
    // Report row this drawer opens from, which shows the date they actually left.
    // `endedAt` is that date (null while the subscription is live).
    const ended = sub.status !== 'active' && !!sub.endedAt;
    const termDate = ended ? sub.endedAt : sub.renews;
    const du = daysUntil(termDate, nowD);
    // Lifetime value = sum of successful payments (first + renewals) from the API.
    const ltv = sub.ltv || 0;
    // Two groups, rendered one after the other. OUTBOUND = threads the member spoke
    // in; those carry the response figures above. INBOUND = threads somebody opened
    // and the member never answered — listed since they are real conversations (and
    // a paying member ignoring them is the finding this page is for), but kept below
    // a divider and out of every metric. Mixing them into one list would make the
    // drawer disagree with its own "Response rate", which counts outbound only.
    //
    // Both lists are ordered by RECENCY — newest conversation first — like every
    // other chat list in the product. The outbound list used to float every
    // unanswered thread above every answered one and only sort by recency inside
    // those two buckets, which buried recent activity under stale outreach: Karan
    // has 288 outbound threads, 154 of them unanswered, so his most recent
    // conversations (the bot chats from 29 Jul) sat at positions 155-162 while the
    // top of the drawer showed 25 Jul and older. Reading it as "the recent chats
    // are missing" was the correct reading of what it showed (ADMIN-11).
    // Unanswered threads are not lost by this: each one still carries the red
    // "No reply" tag on its row, and the member's response rate / status is
    // computed from all of them regardless of the order they are listed in.
    const byRecency = (a, b) => lastMsgTime(b) - lastMsgTime(a);
    const convs = m.convos.filter((c) => !c.inbound).sort(byRecency);
    const inbound = m.convos.filter((c) => c.inbound).sort(byRecency);
    // Real people the member ignored, called out separately from bot blasts: one
    // unanswered human is a different signal from thirty unanswered bot pings.
    const inboundReal = inbound.filter((c) => !c.matchIsBot).length;
    return (
      <>
        <div className="dhead">
          <div className="row1">
            <Avatar src={m.profilePicture} initials={m.initials} online={m.online} />
            <div>
              <h2>{m.name}{m.isDeletedUser && <span className="del-tag" title="Account deleted — subscription still counted">deleted</span>}</h2>
              <div className="meta">
                <span className={`plan ${planCls(m.plan)}`}>{m.planName || m.plan}</span> &nbsp;·&nbsp; last active {k.last > 0 ? ago(new Date(k.last).toISOString(), nowD) : '—'} &nbsp;·&nbsp;{' '}
                <span className="vprofile" role="button" tabIndex={0} onClick={(e) => openProfile(e, m.id)}>View profile →</span>
              </div>
            </div>
            <button className="x" aria-label="Close" onClick={onClose}>✕</button>
          </div>
          <div className="dstats">
            <div className="dstat"><div className="l">Response rate</div>
              <div className={`v ${vcls}`} title={rateKnown ? undefined : UNKNOWN_TIP(chatLost)}>{rateKnown ? `${rate}%` : '—'}</div>
            </div>
            <div className="dstat"><div className="l">Median 1st reply</div>
              <div className="v" title={chatLost ? UNKNOWN_TIP(true) : undefined}>{k.med != null ? fmtDur(k.med) : '—'}</div>
            </div>
            <div className="dstat"><div className="l">Sent / Replies</div>
              <div className="v" title={chatLost ? UNKNOWN_TIP(true) : undefined}>
                {chatLost ? '—' : <>{k.sent}<span style={{ fontSize: 14, color: 'var(--soft)' }}> / {k.replies}</span></>}
              </div>
            </div>
          </div>
          <div className="subcard">
            <div className="sc-row">
              {/* `m.planName` is the real plan ("Luxury", "Gold · Men"). This used
                  to print cap(m.plan), i.e. "Diamond"/"Platinum" — names with no
                  matching plan in the catalogue. The price is the member's OWN
                  monthly rate in INR, from the ledger, not the plan's list price. */}
              <div className="sc-i"><div className="l">Plan</div><div className="v2">{m.planName || cap(m.plan)} · {money(monthlyOf(sub), sub.currency)}/mo</div></div>
              <div className="sc-i"><div className="l">Subscription</div><div className="v2">{subPill(sub.status)}</div></div>
              <div className="sc-i"><div className="l">Account</div><div className="v2">{acctPill(m.acct)}</div></div>
            </div>
            <div className="sc-row">
              <div className="sc-i"><div className="l">Member since</div><div className="v2">{fmtDate(sub.since)} · {monthsSince(sub.since, nowD)} mo</div></div>
              <div className="sc-i">
                <div className="l">{ended ? (sub.status === 'expired' ? 'Expired' : 'Ended') : 'Renews'}</div>
                <div className="v2">{fmtDate(termDate)} · {du >= 0 ? 'in ' + du + 'd' : Math.abs(du) + 'd ago'}</div>
              </div>
              <div className="sc-i"><div className="l">Lifetime value</div><div className="v2">{money(ltv, sub.currency)}</div></div>
            </div>
          </div>
        </div>
        <div className="dbody">
          <div className="dsec">{gone ? 'Conversations' : `${convs.length} conversations · who they messaged`}</div>
          {convs.map((c) => renderConv(c, m.name))}
          {/* Spoke to nobody, but people spoke to them. Without this the section
              above would be a bare heading reading "0 conversations". */}
          {!convs.length && !!inbound.length && (
            <div style={{ padding: '4px 0 2px', color: 'var(--soft)', maxWidth: 460 }}>
              This member has never started a conversation or replied to one.
            </div>
          )}
          {!!inbound.length && (
            <>
              <div className="convsplit">
                Inbound only · never replied · {inbound.length}
                <span className="hint">
                  {inboundReal
                    ? `${inboundReal} from real members — not counted in the response rate above`
                    : 'all from bots — not counted in the response rate above'}
                </span>
              </div>
              {inbound.map((c) => renderConv(c, m.name))}
            </>
          )}
          {!m.convos.length && (
            <div className="empty" style={{ padding: '24px 0', color: 'var(--soft)', maxWidth: 460 }}>
              {gone ? DELETED_CHAT_MSG : 'No conversations for this member.'}
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="met-scope">
      <style>{scopedCss}</style>

      <div className={`overlay ${open ? 'open' : ''}`} onClick={onClose} />
      <aside className={`drawer ${open ? 'open' : ''}`} aria-label="Member detail" aria-hidden={!open}>
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div className="dhead">
              <div className="row1">
                <div><h2>Loading…</h2></div>
                <button className="x" aria-label="Close" onClick={onClose}>✕</button>
              </div>
            </div>
            <div className="dbody" style={{ display: 'grid', placeItems: 'center' }}>
              <span style={{ color: 'var(--soft)' }}>Loading member engagement…</span>
            </div>
          </div>
        ) : member ? (
          renderDrawer()
        ) : open ? (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div className="dhead">
              <div className="row1">
                {fallback ? (
                  <Avatar src={fallback.profilePicture} initials={initialsOf(fallback.name)} />
                ) : null}
                <div>
                  <h2>
                    {fallback && fallback.name ? fallback.name : 'No engagement data'}
                    {fallback && fallback.isDeletedUser && <span className="del-tag" title="Account deleted — subscription still counted">deleted</span>}
                  </h2>
                  {fallback && (
                    <div className="meta">
                      {fallback.isDeletedUser ? 'Subscriber · chat history deleted with the account' : 'Subscriber · no messaging history'}
                    </div>
                  )}
                </div>
                <button className="x" aria-label="Close" onClick={onClose}>✕</button>
              </div>
            </div>
            <div className="dbody" style={{ display: 'grid', placeItems: 'center', textAlign: 'center' }}>
              <span style={{ color: 'var(--soft)', maxWidth: 380 }}>
                {fallback && fallback.isDeletedUser
                  ? DELETED_CHAT_MSG
                  : 'This member has no tracked conversations to show.'}
              </span>
            </div>
          </div>
        ) : null}
      </aside>

      <Modal open={!!profileUserId} onClose={closeProfile} aria-labelledby="member-profile-modal" sx={{ zIndex: 1500 }}>
        <Box sx={profileModalStyle}>
          <Box
            sx={{
              position: 'sticky',
              top: '0px',
              backgroundColor: 'white',
              zIndex: 10,
              padding: '10px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <IconButton sx={{ position: 'absolute', top: 10, right: 0 }} onClick={closeProfile}>
              <CloseIcon />
            </IconButton>
          </Box>
          {profileUserId && <UserProfileMainPage userId={profileUserId} />}
        </Box>
      </Modal>
    </div>
  );
};

export default MemberEngagementDrawer;
